import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

interface PromptDialogProps {
  title: string;
  message?: string | React.ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmButtonClass?: string;
  isLoading?: boolean;
  children?: React.ReactNode;
}

const PromptDialog: React.FC<PromptDialogProps> = ({
  title,
  message,
  onCancel,
  onConfirm,
  confirmText,
  cancelText,
  confirmButtonClass = "bg-red-500 hover:bg-red-600 text-white",
  isLoading = false,
  children,
}) => {
  const { t } = useTranslation();

  return createPortal(
    <div className="fixed top-0 left-0 w-full h-full bg-black/50 flex justify-center items-center z-[99]">
      <div className="w-[90%] md:w-[70%] lg:w-1/2 bg-white z-[100] shadow-lg">
        <div className="bg-red-500 text-white text-center py-3 px-5 font-semibold text-lg">
          {title}
        </div>

        {/* Content */}
        <div className="p-5">
          {message && (
            <div className="mb-4">
              {typeof message === "string" ? (
                <p className="text-center">{message}</p>
              ) : (
                message
              )}
            </div>
          )}

          {children}

          <div className="flex justify-between items-center space-x-4 mt-6">
            <button
              onClick={onCancel}
              className="w-full border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 py-2 px-4 rounded"
              disabled={isLoading}
            >
              {cancelText || t("_generic.cancel")}
            </button>

            <button
              onClick={onConfirm}
              className={`w-full py-2 px-4 rounded ${confirmButtonClass}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {confirmText || t("_generic.proceed")}
                </span>
              ) : (
                confirmText || t("_generic.proceed")
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.querySelector("#dialog")!
  );
};

export default PromptDialog;
